import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  User, Bell, Settings2, Shield, Save, Eye, EyeOff,
  Monitor, Smartphone, MapPin, CheckCircle2, XCircle,
  AlertTriangle,
} from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProfileForm {
  name: string;
  phone: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface NotificationPrefs {
  capaReminder: boolean;
  capaReminderDays: number;
  certExpiry: boolean;
  certExpiryDays: number;
  auditReminder: boolean;
  equipmentCalibration: boolean;
  newTestRequest: boolean;
  systemMaintenance: boolean;
  channelEmail: boolean;
  channelInApp: boolean;
}

interface SystemConfig {
  defaultLab: string;
  dateFormat: string;
  timezone: string;
  dataRetention: string;
  gmpMode: boolean;
  cfrPart11: boolean;
  systemName: string;
  orgName: string;
}

interface SecuritySettings {
  twoFactor: boolean;
  sessionTimeout: string;
}

interface LoginActivity {
  id: string;
  ip: string;
  device: string;
  location: string;
  time: string;
  status: 'success' | 'failed';
  current?: boolean;
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_LOGIN_ACTIVITY: LoginActivity[] = [
  { id: 'l1', ip: '203.88.14.21', device: 'Chrome · Windows 11', location: 'Mumbai, IN', time: '2026-04-04 09:12', status: 'success', current: true },
  { id: 'l2', ip: '203.88.14.21', device: 'Chrome · Windows 11', location: 'Mumbai, IN', time: '2026-04-03 14:30', status: 'success' },
  { id: 'l3', ip: '112.43.55.8', device: 'Safari · iOS 17', location: 'Pune, IN', time: '2026-04-02 11:00', status: 'success' },
  { id: 'l4', ip: '45.250.10.3', device: 'Firefox · macOS', location: 'Unknown', time: '2026-04-01 03:22', status: 'failed' },
  { id: 'l5', ip: '203.88.14.21', device: 'Chrome · Windows 11', location: 'Mumbai, IN', time: '2026-03-31 08:45', status: 'success' },
];

// ─── Toggle Switch ────────────────────────────────────────────────────────────

interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}

function Toggle({ checked, onChange, disabled }: ToggleProps) {
  return (
    <label className="relative inline-flex items-center cursor-pointer select-none">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className={`
        w-10 h-5 rounded-full transition-colors duration-200
        peer-checked:bg-teal-600 bg-gray-300
        after:content-[''] after:absolute after:top-0.5 after:left-0.5
        after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all
        peer-checked:after:translate-x-5
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `} />
    </label>
  );
}

// ─── Section Heading ──────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="font-serif font-semibold text-sm text-text mb-3 pt-2">{children}</h3>;
}

// ─── Input ────────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-text-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

const INPUT_CLS = 'w-full px-3 py-2 text-sm border border-border rounded-lg bg-bg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-teal-500/30';
const READONLY_CLS = 'w-full px-3 py-2 text-sm border border-border rounded-lg bg-card2 text-text-muted cursor-not-allowed';
const SELECT_CLS = 'w-full px-3 py-2 text-sm border border-border rounded-lg bg-bg text-text';

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type TabId = 'profile' | 'notifications' | 'system' | 'security';

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'system', label: 'System', icon: Settings2 },
  { id: 'security', label: 'Security', icon: Shield },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [showPw, setShowPw] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Profile state
  const [profile, setProfile] = useState<ProfileForm>({
    name: user?.name ?? '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Notification prefs
  const [notif, setNotif] = useState<NotificationPrefs>({
    capaReminder: true, capaReminderDays: 7,
    certExpiry: true, certExpiryDays: 30,
    auditReminder: true,
    equipmentCalibration: true,
    newTestRequest: true,
    systemMaintenance: false,
    channelEmail: true,
    channelInApp: true,
  });

  // System config
  const [sysConfig, setSysConfig] = useState<SystemConfig>({
    defaultLab: 'Lab A',
    dateFormat: 'DD/MM/YYYY',
    timezone: 'Asia/Kolkata',
    dataRetention: '5yr',
    gmpMode: true,
    cfrPart11: true,
    systemName: 'Quantum Kairoz QMS',
    orgName: 'Forge Quantum Solutions',
  });

  // Security settings
  const [security, setSecurity] = useState<SecuritySettings>({
    twoFactor: false,
    sessionTimeout: '1hr',
  });

  // Mutations
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const profileMutation = useMutation({
    mutationFn: (data: Partial<ProfileForm>) => api.patch('/users/me', data),
    onSuccess: () => showToast('Profile saved successfully.'),
  });

  const notifMutation = useMutation({
    mutationFn: (data: NotificationPrefs) => api.patch('/users/me/notification-preferences', data),
    onSuccess: () => showToast('Notification preferences saved.'),
  });

  const sysMutation = useMutation({
    mutationFn: (data: SystemConfig) => api.patch('/system/config', data),
    onSuccess: () => showToast('System configuration saved.'),
  });

  const securityMutation = useMutation({
    mutationFn: (data: SecuritySettings) => api.patch('/users/me/security', data),
    onSuccess: () => showToast('Security settings saved.'),
  });

  const initials = (user?.name ?? 'U').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  // ─── Tab content ────────────────────────────────────────────────────────────

  const renderProfile = () => (
    <div className="space-y-6 max-w-lg">
      {/* Avatar */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center font-serif font-bold text-teal-700 text-xl">
          {initials}
        </div>
        <div>
          <p className="font-semibold text-text">{user?.name}</p>
          <p className="text-xs text-text-muted capitalize">{user?.role?.replace(/_/g, ' ')}</p>
        </div>
      </div>

      <SectionTitle>Personal Information</SectionTitle>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Full Name">
          <input
            className={INPUT_CLS}
            value={profile.name}
            onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
          />
        </Field>
        <Field label="Email">
          <input className={READONLY_CLS} value={user?.email ?? ''} readOnly />
        </Field>
        <Field label="Role">
          <input className={READONLY_CLS} value={(user?.role ?? '').replace(/_/g, ' ')} readOnly />
        </Field>
        <Field label="Phone">
          <input
            className={INPUT_CLS}
            value={profile.phone}
            onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
            placeholder="+91 98765 43210"
          />
        </Field>
      </div>

      <SectionTitle>Change Password</SectionTitle>
      <div className="space-y-3">
        <Field label="Current Password">
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              className={INPUT_CLS}
              value={profile.currentPassword}
              onChange={(e) => setProfile((p) => ({ ...p, currentPassword: e.target.value }))}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted"
              onClick={() => setShowPw((v) => !v)}
            >
              {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="New Password">
            <input
              type="password"
              className={INPUT_CLS}
              value={profile.newPassword}
              onChange={(e) => setProfile((p) => ({ ...p, newPassword: e.target.value }))}
            />
          </Field>
          <Field label="Confirm Password">
            <input
              type="password"
              className={INPUT_CLS}
              value={profile.confirmPassword}
              onChange={(e) => setProfile((p) => ({ ...p, confirmPassword: e.target.value }))}
            />
          </Field>
        </div>
        {profile.newPassword && profile.confirmPassword && profile.newPassword !== profile.confirmPassword && (
          <p className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle size={11} /> Passwords do not match</p>
        )}
      </div>

      <button
        onClick={() => profileMutation.mutate({ name: profile.name, phone: profile.phone })}
        disabled={profileMutation.isPending}
        className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
      >
        <Save size={14} /> {profileMutation.isPending ? 'Saving…' : 'Save Changes'}
      </button>
    </div>
  );

  const renderNotifications = () => (
    <div className="space-y-6 max-w-lg">
      <SectionTitle>Alert Triggers</SectionTitle>
      <div className="space-y-4">
        {[
          {
            key: 'capaReminder' as const, label: 'CAPA Due Date Reminders',
            desc: 'Notify before CAPA due dates', hasDays: true, daysKey: 'capaReminderDays' as const,
          },
          {
            key: 'certExpiry' as const, label: 'Certification Expiry Alerts',
            desc: 'Notify before certification expiry', hasDays: true, daysKey: 'certExpiryDays' as const,
          },
          {
            key: 'auditReminder' as const, label: 'Audit Schedule Reminders',
            desc: 'Notify when audits are approaching', hasDays: false, daysKey: null,
          },
          {
            key: 'equipmentCalibration' as const, label: 'Equipment Calibration Due',
            desc: 'Notify when equipment calibration is due', hasDays: false, daysKey: null,
          },
          {
            key: 'newTestRequest' as const, label: 'New Test Request Assigned',
            desc: 'Notify when a test request is assigned to me', hasDays: false, daysKey: null,
          },
          {
            key: 'systemMaintenance' as const, label: 'System Maintenance Alerts',
            desc: 'Notify about scheduled system downtime', hasDays: false, daysKey: null,
          },
        ].map(({ key, label, desc, hasDays, daysKey }) => (
          <div key={key} className="flex items-start justify-between gap-4 p-3 rounded-xl border border-border bg-bg">
            <div className="flex-1">
              <p className="text-sm font-medium text-text">{label}</p>
              <p className="text-xs text-text-muted mt-0.5">{desc}</p>
              {hasDays && notif[key] && daysKey && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-text-muted">Days before:</span>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    className="w-16 px-2 py-1 text-xs border border-border rounded-lg bg-card text-text"
                    value={notif[daysKey]}
                    onChange={(e) => setNotif((n) => ({ ...n, [daysKey]: Number(e.target.value) }))}
                  />
                </div>
              )}
            </div>
            <Toggle
              checked={notif[key]}
              onChange={(v) => setNotif((n) => ({ ...n, [key]: v }))}
            />
          </div>
        ))}
      </div>

      <SectionTitle>Delivery Channels</SectionTitle>
      <div className="space-y-3">
        {[
          { key: 'channelEmail' as const, label: 'Email Notifications', desc: 'Receive alerts via email' },
          { key: 'channelInApp' as const, label: 'In-App Notifications', desc: 'Show alerts in the notification panel' },
        ].map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between p-3 rounded-xl border border-border bg-bg">
            <div>
              <p className="text-sm font-medium text-text">{label}</p>
              <p className="text-xs text-text-muted">{desc}</p>
            </div>
            <Toggle checked={notif[key]} onChange={(v) => setNotif((n) => ({ ...n, [key]: v }))} />
          </div>
        ))}
      </div>

      <button
        onClick={() => notifMutation.mutate(notif)}
        disabled={notifMutation.isPending}
        className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
      >
        <Save size={14} /> {notifMutation.isPending ? 'Saving…' : 'Save Preferences'}
      </button>
    </div>
  );

  const renderSystem = () => {
    if (!isAdmin) {
      return (
        <div className="max-w-lg">
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl text-sm text-orange-700 flex items-center gap-2">
            <AlertTriangle size={15} /> System configuration is restricted to administrators.
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6 max-w-lg">
        <SectionTitle>Lab Configuration</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Default Lab">
            <select className={SELECT_CLS} value={sysConfig.defaultLab} onChange={(e) => setSysConfig((s) => ({ ...s, defaultLab: e.target.value }))}>
              {['Lab A', 'Lab B', 'Lab C', 'Lab D', 'Lab E'].map((l) => <option key={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Date Format">
            <select className={SELECT_CLS} value={sysConfig.dateFormat} onChange={(e) => setSysConfig((s) => ({ ...s, dateFormat: e.target.value }))}>
              {['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Time Zone">
            <select className={SELECT_CLS} value={sysConfig.timezone} onChange={(e) => setSysConfig((s) => ({ ...s, timezone: e.target.value }))}>
              {['Asia/Kolkata', 'UTC', 'America/New_York', 'Europe/London', 'Asia/Tokyo'].map((tz) => <option key={tz}>{tz}</option>)}
            </select>
          </Field>
          <Field label="Data Retention">
            <select className={SELECT_CLS} value={sysConfig.dataRetention} onChange={(e) => setSysConfig((s) => ({ ...s, dataRetention: e.target.value }))}>
              {[{ v: '1yr', l: '1 Year' }, { v: '3yr', l: '3 Years' }, { v: '5yr', l: '5 Years' }, { v: '7yr', l: '7 Years' }].map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </Field>
        </div>

        <SectionTitle>Compliance Mode</SectionTitle>
        <div className="space-y-3">
          {[
            { key: 'gmpMode' as const, label: 'GMP Mode', desc: 'Enforce Good Manufacturing Practice controls and audit trails' },
            { key: 'cfrPart11' as const, label: '21 CFR Part 11', desc: 'Enable electronic records and electronic signature compliance' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between p-3 rounded-xl border border-border bg-bg">
              <div>
                <p className="text-sm font-medium text-text">{label}</p>
                <p className="text-xs text-text-muted">{desc}</p>
              </div>
              <Toggle checked={sysConfig[key]} onChange={(v) => setSysConfig((s) => ({ ...s, [key]: v }))} />
            </div>
          ))}
        </div>

        <SectionTitle>Organization</SectionTitle>
        <div className="grid grid-cols-1 gap-4">
          <Field label="System Name">
            <input className={INPUT_CLS} value={sysConfig.systemName} onChange={(e) => setSysConfig((s) => ({ ...s, systemName: e.target.value }))} />
          </Field>
          <Field label="Organization Name">
            <input className={INPUT_CLS} value={sysConfig.orgName} onChange={(e) => setSysConfig((s) => ({ ...s, orgName: e.target.value }))} />
          </Field>
        </div>

        <button
          onClick={() => sysMutation.mutate(sysConfig)}
          disabled={sysMutation.isPending}
          className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
        >
          <Save size={14} /> {sysMutation.isPending ? 'Saving…' : 'Save Configuration'}
        </button>
      </div>
    );
  };

  const renderSecurity = () => (
    <div className="space-y-6 max-w-lg">
      <SectionTitle>Authentication</SectionTitle>
      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-bg">
          <div>
            <p className="text-sm font-medium text-text">Two-Factor Authentication</p>
            <p className="text-xs text-text-muted">Add an extra layer of security using an authenticator app</p>
          </div>
          <Toggle checked={security.twoFactor} onChange={(v) => setSecurity((s) => ({ ...s, twoFactor: v }))} />
        </div>
        <div className="p-3 rounded-xl border border-border bg-bg">
          <label className="block text-sm font-medium text-text mb-2">Session Timeout</label>
          <select
            className={SELECT_CLS}
            value={security.sessionTimeout}
            onChange={(e) => setSecurity((s) => ({ ...s, sessionTimeout: e.target.value }))}
          >
            {[
              { v: '15min', l: '15 Minutes' }, { v: '30min', l: '30 Minutes' },
              { v: '1hr', l: '1 Hour' }, { v: '4hr', l: '4 Hours' }, { v: '8hr', l: '8 Hours' },
            ].map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
      </div>

      <SectionTitle>Login Activity</SectionTitle>
      <div className="card overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-bg border-b border-border">
            <tr>
              <th className="text-left px-3 py-2 text-text-muted font-medium">IP / Device</th>
              <th className="text-left px-3 py-2 text-text-muted font-medium hidden sm:table-cell">Location</th>
              <th className="text-left px-3 py-2 text-text-muted font-medium">Time</th>
              <th className="text-left px-3 py-2 text-text-muted font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_LOGIN_ACTIVITY.map((a) => (
              <tr key={a.id} className="border-b border-border last:border-0 hover:bg-bg/50">
                <td className="px-3 py-2.5">
                  <p className="font-mono text-text">{a.ip}</p>
                  <p className="text-text-muted">{a.device}</p>
                  {a.current && <span className="px-1.5 py-px bg-teal-100 text-teal-700 text-[10px] rounded-full font-semibold">Current</span>}
                </td>
                <td className="px-3 py-2.5 hidden sm:table-cell">
                  <span className="flex items-center gap-1 text-text-muted"><MapPin size={10} />{a.location}</span>
                </td>
                <td className="px-3 py-2.5 text-text-muted">{a.time}</td>
                <td className="px-3 py-2.5">
                  {a.status === 'success'
                    ? <CheckCircle2 size={13} className="text-green-600" />
                    : <XCircle size={13} className="text-red-500" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionTitle>Active Sessions</SectionTitle>
      <div className="space-y-2">
        {MOCK_LOGIN_ACTIVITY.filter((a) => a.status === 'success').slice(0, 3).map((a) => (
          <div key={a.id} className="flex items-center justify-between p-3 rounded-xl border border-border bg-bg">
            <div className="flex items-center gap-3">
              {a.device.includes('iOS') ? <Smartphone size={15} className="text-text-muted" /> : <Monitor size={15} className="text-text-muted" />}
              <div>
                <p className="text-sm text-text">{a.device}</p>
                <p className="text-xs text-text-muted">{a.ip} · {a.location}</p>
              </div>
              {a.current && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded-full">This session</span>}
            </div>
            {!a.current && (
              <button className="text-xs text-red-500 hover:text-red-700 font-medium border border-red-200 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors">
                Revoke
              </button>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={() => securityMutation.mutate(security)}
        disabled={securityMutation.isPending}
        className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
      >
        <Save size={14} /> {securityMutation.isPending ? 'Saving…' : 'Save Security Settings'}
      </button>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Settings"
        subtitle="Account · Notifications · System configuration"
      />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-gray-900 text-white px-4 py-3 rounded-xl shadow-lg text-sm">
          <CheckCircle2 size={15} className="text-green-400" />
          {toast}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <nav className="flex lg:flex-col gap-1 lg:w-48 flex-shrink-0">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors text-left ${
                activeTab === id
                  ? 'bg-teal-600 text-white'
                  : 'text-text-muted hover:bg-bg hover:text-text'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </nav>

        {/* Tab Content */}
        <div className="flex-1 card p-6 min-h-[400px]">
          {activeTab === 'profile' && renderProfile()}
          {activeTab === 'notifications' && renderNotifications()}
          {activeTab === 'system' && renderSystem()}
          {activeTab === 'security' && renderSecurity()}
        </div>
      </div>
    </div>
  );
}
